<script setup lang="ts">
/**
 * The Footer Proceed Button
 * --------------------------------
 * Proceed/Next button in the footer
 * - can be a normal rectangle button or a floating circle button
 * - shows a loading spinner if the proceed action takes longer than 1s
 */

const {
    forwardCallback,
    forwardLabel,
    layout,
    canProceed,
    isProceedDisabledVisually,
    isProceedPending,
} = useFooterState();

const isProceedLoading = ref(false);

watch(isProceedPending, (pending, _, onCleanup) => {
    isProceedLoading.value = false;
    if (!pending) {
        return;
    }

    const loadingTimeout = setTimeout(() => {
        isProceedLoading.value = true;
    }, 1000);
    const safetyTimeout = setTimeout(() => {
        isProceedPending.value = false;
    }, 10000);

    onCleanup(() => {
        clearTimeout(loadingTimeout);
        clearTimeout(safetyTimeout);
    });
}, { immediate: true });

function proceed() {
    if (isProceedPending.value) {
        return;
    }

    isProceedPending.value = true;

    if (forwardCallback.value) {
        forwardCallback.value();
    }
}

const buttonLayout = computed(() => layout.value === 'floating' ? 'circle' : 'default');

const classes = computed(() => {
    const basicClasses = [
        'relative',
        'whitespace-nowrap',
        'overflow-hidden',
        'typo-button',
        'bg-brand-a',
        'text-brand-b',
        'disabled:bg-brand-a-2',
        'disabled:text-brand-a-3',
        'data-[disabled-visually=true]:bg-brand-a-2',
        'data-[disabled-visually=true]:text-brand-a-3',
        'hover:bg-shade-2',
        'transition-colors',
    ];

    const defaultClasses = [
        'px-5',
        'py-3',
        'rounded-sm',
    ];

    const circleClasses = [
        'flex',
        'items-center',
        'justify-center',
        'rounded-full',
        'aspect-square',
        'p-4',
        'size-[64px]',
    ];

    switch (buttonLayout.value) {
        case 'circle':
            return [
                ...basicClasses,
                ...circleClasses,
            ];

        case 'default':
        default:
            return [
                ...basicClasses,
                ...defaultClasses,
            ];
    }
});
</script>

<template>
    <button
        v-if="!!forwardCallback"
        data-t-button-proceed
        :class="classes"
        :disabled="!canProceed || isProceedPending"
        :data-disabled-visually="isProceedDisabledVisually"
        data-sentry-id="Footer Next Button"
        @click="proceed"
    >
        <span class="pointer-events-none">{{ forwardLabel }}</span>
        <Transition name="fade">
            <span v-if="isProceedLoading" class="absolute top-0 left-0 flex size-full items-center justify-center bg-brand-a-2 opacity-90">
                <LoadingSpinner class="size-5!" />
            </span>
        </Transition>
    </button>
</template>
